import React, { useState, useEffect } from "react";
import {
  FiHelpCircle,
  FiInfo,
  FiUsers,
  FiDatabase,
  FiShield,
  FiGlobe,
  FiMail,
  FiExternalLink,
  FiStar,
  FiHeart,
} from "react-icons/fi";
import "./HelpPage.css";
import SideBar from "../Dashboard/SideBar";
import { authAPI } from "../../services/api";
import { siteConfig, getSiteName, getPageTitle } from "../../config/siteConfig";

const translations = {
  ko: {
    langLabel: "한국어",
    heading: "도움말 & 서비스 안내",
    subtitle: "연구팀을 위한 맞춤형 eCRF 제작 플랫폼",
    welcome: {
      title: "MyECRF에 오신 것을 환영합니다!",
      description:
        "연구팀의 요구사항에 맞춰 완전히 커스터마이징 가능한 eCRF 및 데이터 수집 프로젝트 제작 플랫폼입니다. 팀과의 소통을 통해 최적의 솔루션을 제공합니다.",
    },
    features: [
      {
        icon: "shield",
        title: "완전한 커스터마이징",
        description:
          "연구팀의 특별한 요구사항에 맞춰 모든 기능과 디자인을 자유롭게 커스터마이징할 수 있습니다.",
      },
      {
        icon: "globe",
        title: "팀 중심의 소통",
        description:
          "프로젝트 시작부터 완료까지 연구팀과 긴밀히 소통하며 최적의 솔루션을 함께 만들어갑니다.",
      },
      {
        icon: "database",
        title: "전문적인 eCRF 제작",
        description:
          "임상연구, 설문조사, 데이터 수집 등 다양한 연구 목적에 맞는 전문적인 eCRF를 제작합니다.",
      },
      {
        icon: "users",
        title: "원스톱 서비스",
        description:
          "기획, 설계, 개발, 배포까지 연구 프로젝트에 필요한 모든 과정을 한 번에 제공합니다.",
      },
    ],
    betaInfo: {
      title: "서비스 특징",
      points: [
        "연구팀의 개별 요구사항에 100% 맞춤형 제작이 가능합니다.",
        "임상 현장 경험이 풍부한 전문가가 직접 설계하고 개발합니다.",
        "프로젝트 특성에 맞는 최적의 데이터 수집 방식을 제안합니다.",
        "지속적인 기술 지원과 유지보수 서비스를 제공합니다.",
      ],
    },
    contact: {
      title: "문의하기",
      description: "프로젝트 문의나 상담이 필요하시면 언제든 연락해주세요.",
      email: "check@idea2m.com",
      website: "https://myecrf.com",
    },
    footer: {
      thanks: "더 나은 연구 환경을 위해 함께하겠습니다!",
      company: "아이디어2마켓 주식회사",
    },
  },
  en: {
    langLabel: "English",
    heading: "Help & Service Information",
    subtitle: "Custom eCRF Development Platform for Research Teams",
    welcome: {
      title: "Welcome to MyECRF!",
      description:
        "A fully customizable eCRF and data collection project platform tailored to research teams' requirements. We provide optimal solutions through close communication with your team.",
    },
    features: [
      {
        icon: "shield",
        title: "Complete Customization",
        description:
          "All features and designs can be freely customized to meet your research team's specific requirements.",
      },
      {
        icon: "globe",
        title: "Team-Centered Communication",
        description:
          "We work closely with research teams from project start to completion to create optimal solutions together.",
      },
      {
        icon: "database",
        title: "Professional eCRF Development",
        description:
          "We create professional eCRFs for various research purposes including clinical studies, surveys, and data collection.",
      },
      {
        icon: "users",
        title: "One-Stop Service",
        description:
          "We provide all processes needed for research projects from planning and design to development and deployment.",
      },
    ],
    betaInfo: {
      title: "Service Features",
      points: [
        "100% customized development possible according to individual research team requirements.",
        "Directly designed and developed by experts with extensive clinical field experience.",
        "We propose optimal data collection methods suited to project characteristics.",
        "We provide continuous technical support and maintenance services.",
      ],
    },
    contact: {
      title: "Contact Us",
      description:
        "Please feel free to contact us anytime for project inquiries or consultations.",
      email: "check@idea2m.com",
      website: "https://myecrf.com",
    },
    footer: {
      thanks: "We will work together for a better research environment!",
      company: "Idea2Market Co., Ltd.",
    },
  },
  zh: {
    langLabel: "中文",
    heading: "帮助 & 服务信息",
    subtitle: "为研究团队定制的eCRF开发平台",
    welcome: {
      title: "欢迎使用 MyECRF！",
      description:
        "根据研究团队需求完全可定制的eCRF和数据收集项目平台。通过与团队的密切沟通提供最佳解决方案。",
    },
    features: [
      {
        icon: "shield",
        title: "完全定制化",
        description: "所有功能和设计都可以根据研究团队的特殊需求自由定制。",
      },
      {
        icon: "globe",
        title: "以团队为中心的沟通",
        description:
          "从项目开始到完成，与研究团队密切沟通，共同创造最佳解决方案。",
      },
      {
        icon: "database",
        title: "专业eCRF制作",
        description:
          "为临床研究、问卷调查、数据收集等各种研究目的制作专业的eCRF。",
      },
      {
        icon: "users",
        title: "一站式服务",
        description: "从规划、设计到开发、部署，提供研究项目所需的所有流程。",
      },
    ],
    betaInfo: {
      title: "服务特色",
      points: [
        "可根据研究团队的个别需求进行100%定制开发。",
        "由具有丰富临床现场经验的专家直接设计和开发。",
        "提出适合项目特性的最佳数据收集方式。",
        "提供持续的技术支持和维护服务。",
      ],
    },
    contact: {
      title: "联系我们",
      description: "如需项目咨询或商谈，请随时联系我们。",
      email: "check@idea2m.com",
      website: "https://myecrf.com",
    },
    footer: {
      thanks: "我们将为更好的研究环境而共同努力！",
      company: "创意二市场有限公司",
    },
  },
  ja: {
    langLabel: "日本語",
    heading: "ヘルプ & サービス案内",
    subtitle: "研究チーム向けカスタムeCRF開発プラットフォーム",
    welcome: {
      title: "MyECRFへようこそ！",
      description:
        "研究チームの要求に合わせて完全にカスタマイズ可能なeCRFおよびデータ収集プロジェクトプラットフォームです。チームとの密接なコミュニケーションを通じて最適なソリューションを提供します。",
    },
    features: [
      {
        icon: "shield",
        title: "完全なカスタマイズ",
        description:
          "研究チームの特別な要求に合わせて、すべての機能とデザインを自由にカスタマイズできます。",
      },
      {
        icon: "globe",
        title: "チーム中心のコミュニケーション",
        description:
          "プロジェクト開始から完了まで研究チームと密接にコミュニケーションを取り、最適なソリューションを一緒に作り上げます。",
      },
      {
        icon: "database",
        title: "専門的なeCRF制作",
        description:
          "臨床研究、アンケート調査、データ収集など様々な研究目的に合わせた専門的なeCRFを制作します。",
      },
      {
        icon: "users",
        title: "ワンストップサービス",
        description:
          "企画、設計、開発、デプロイまで研究プロジェクトに必要なすべてのプロセスを一度に提供します。",
      },
    ],
    betaInfo: {
      title: "サービスの特徴",
      points: [
        "研究チームの個別要求に100%カスタマイズ制作が可能です。",
        "豊富な臨床現場経験を持つ専門家が直接設計・開発します。",
        "プロジェクト特性に合った最適なデータ収集方式を提案します。",
        "継続的な技術サポートとメンテナンスサービスを提供します。",
      ],
    },
    contact: {
      title: "お問い合わせ",
      description:
        "プロジェクトのお問い合わせやご相談が必要でしたら、いつでもご連絡ください。",
      email: "check@idea2m.com",
      website: "https://myecrf.com",
    },
    footer: {
      thanks: "より良い研究環境のために一緒に取り組みます！",
      company: "アイデア2マーケット株式会社",
    },
  },
};

const HelpPage = () => {
  const [lang, setLang] = useState("ko");
  const [userInfo, setUserInfo] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const t = translations[lang] || translations.ko;

  useEffect(() => {
    // 페이지 제목 설정
    document.title = getPageTitle("help", {
      language: lang,
      customTitle: t.heading,
    });

    // 사용자 정보 조회
    (async () => {
      try {
        const res = await authAPI.getUserInfo();
        if (res.success) {
          setUserInfo(res.user);
          setSubscriptionInfo(res.subscription);
        }
      } catch (e) {
        console.error("help user info error", e);
      }
    })();
  }, [lang, t.heading]);

  const getFeatureIcon = (iconName) => {
    const iconMap = {
      shield: <FiShield />,
      globe: <FiGlobe />,
      database: <FiDatabase />,
      users: <FiUsers />,
    };
    return iconMap[iconName] || <FiInfo />;
  };

  return (
    <div className="help-layout">
      <SideBar userInfo={userInfo} subscriptionInfo={subscriptionInfo} />

      <div className="help-main-content">
        {/* Header Section */}
        <div className="help-header">
          <div className="help-header-content">
            <div className="help-title-section">
              <FiHelpCircle className="help-main-icon" />
              <div>
                <h1 className="help-title">{t.heading}</h1>
                <p className="help-subtitle">{t.subtitle}</p>
              </div>
            </div>

            {/* Language Selector */}
            <div className="help-lang-selector">
              {Object.entries(translations).map(([code, obj]) => (
                <button
                  key={code}
                  className={`help-lang-btn ${lang === code ? "active" : ""}`}
                  onClick={() => setLang(code)}
                >
                  <FiGlobe className="lang-icon" />
                  {obj.langLabel}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Welcome Section */}
        <div className="help-section">
          <div className="help-welcome-card">
            <div className="welcome-icon">
              <FiHeart />
            </div>
            <h2>{t.welcome.title}</h2>
            <p>{t.welcome.description}</p>
          </div>
        </div>

        {/* Features Section */}
        <div className="help-section">
          <h2 className="section-title">
            <FiStar className="section-icon" />
            {lang === "ko"
              ? "주요 특징"
              : lang === "en"
              ? "Key Features"
              : lang === "zh"
              ? "主要特色"
              : "主な特徴"}
          </h2>
          <div className="features-grid">
            {t.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  {getFeatureIcon(feature.icon)}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Beta Info Section */}
        <div className="help-section">
          <div className="beta-info-card">
            <h2 className="section-title">
              <FiInfo className="section-icon" />
              {t.betaInfo.title}
            </h2>
            <ul className="beta-points">
              {t.betaInfo.points.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Section */}
        <div className="help-section">
          <h2 className="section-title">
            <FiMail className="section-icon" />
            {t.contact.title}
          </h2>
          <div className="contact-description">
            <p>{t.contact.description}</p>
          </div>
          <div className="contact-grid">
            <div className="contact-item">
              <FiMail className="contact-icon" />
              <div>
                <span className="contact-label">Email</span>
                <a href={`mailto:${t.contact.email}`} className="contact-value">
                  {t.contact.email}
                </a>
              </div>
            </div>
            <div className="contact-item">
              <FiExternalLink className="contact-icon" />
              <div>
                <span className="contact-label">Website</span>
                <a
                  href={t.contact.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-value"
                >
                  myecrf.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="help-footer">
          <div className="footer-content">
            <p className="footer-thanks">{t.footer.thanks}</p>
            <p className="footer-company">{t.footer.company}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
